/** Shared clinical-rule configuration for the assessment evaluator and Template API. */

import type { GridOption, GridSchema, GridTarget } from '../utils/helpers/grid-control';
import { getGridCellOptions, getGridCellProperties, getGridOtherValue } from '../utils/helpers/grid-control';

/** ICD code entry from a resolved rule definition. */
export interface ClinicalRuleIcdCode {
  code: string;
  displayOrder?: number;
}

/** Conflict definition entry from a resolved rule definition. */
export interface ClinicalRuleConflictDefinition {
  conflictDefinitionId: number;
  /** Numeric value of RuleConflictType enum (1 = CrossGroup, 2 = MutuallyExclusive). */
  conflictType: number;
  note?: string;
  displayOrder?: number;
  icdCodes: ClinicalRuleIcdCode[];
  sets?: ClinicalRuleConflictSet[];
}

export interface ClinicalRuleConflictSet {
  conflictSetId: number;
  name: string;
  displayOrder: number;
  icdCodes: ClinicalRuleIcdCode[];
}

/** Full detail of a single active Conflicting Diagnosis rule. */
export interface ConflictingDiagnosisClinicalRule {
  ruleId: number;
  ruleGroupId: number;
  ruleName: string;
  ruleGroupName?: string;
  whyConflict: string;
  howToResolve: string;
  /** Numeric string of RuleMatchType enum. */
  matchType?: string;
  /** Numeric string of RuleSeverity enum. */
  severity: string;
  /** Numeric string of RuleCategory enum. */
  category: string;
  isActive: boolean;
  icdCodes: ClinicalRuleIcdCode[];
  conflictDefinitions: ClinicalRuleConflictDefinition[];
}

/** Full detail of a single active Do Not Code rule. */
export interface DoNotCodeClinicalRule {
  ruleId: number;
  ruleGroupId: number;
  ruleName: string;
  ruleGroupName?: string;
  whyConflict: string;
  howToResolve: string;
  matchType?: string;
  severity: string;
  category: string;
  isActive: boolean;
  icdCodes: string[];
}

/** Rule-group metadata used to resolve names without extra API calls. */
export interface ClinicalRulesRuleGroup {
  ruleGroupId: number;
  groupName: string;
}

/** Top-level configuration for Do Not Code, Conflicting Diagnosis, and Documentation Required rules. */
export interface ClinicalRulesConfiguration {
  doNotCodeRuleConfig: {
    enabled: boolean;
    ruleGroups?: ClinicalRulesRuleGroup[];
    rules: DoNotCodeClinicalRule[];
  };
  conflictDiagnosisRuleConfig: {
    enabled: boolean;
    ruleGroups?: ClinicalRulesRuleGroup[];
    rules: ConflictingDiagnosisClinicalRule[];
  };
  documentationRequiredRulesConfig: DocumentationRequiredConfiguration;
}

/** Rule metadata used to evaluate Documentation Required. */
export interface DocumentationRequiredRuleSnapshot {
  icdCodes: string[];
  /** Numeric RuleSeverity: 1=Blocker, 2=Warning, 3=Info */
  severity: number;
  whyConflict: string;
  howToResolve: string;
  ruleGroupId: number;
  groupName: string;
  ruleName: string;
}

/** A single Documentation Required expression row. */
export interface DocumentationRequiredRule {
  id: string;
  /** Null on the first row at its level; AND | OR for subsequent rows. */
  operator: 'OR' | 'AND' | null;
  groupId: number;
  groupName: string;
  ruleIds: number[];
  ruleNames: string[];
  evidenceLinkId: string;
  evidencePathText: string;
  condition: 'EqualTo' | 'NotEqualTo' | 'Any' | 'All';
  values: string[];
  rowGroupId?: string;
}

/** Top-level Documentation Required configuration. */
export interface DocumentationRequiredConfiguration {
  /** Rule metadata keyed by ruleId for evaluation. */
  ruleSnapshot?: Record<string, DocumentationRequiredRuleSnapshot>;
  rules: DocumentationRequiredRule[];
}

export interface GridRuleValue {
  columnId: string;
  kind: 'selected' | 'value' | 'otherText';
  value?: string;
  label?: string;
}

const gridRuleTargetPrefix = 'urn:ccs:grid:v1:';
const gridRuleValuePrefix = 'urn:ccs:value:v1:';

export function isGridRuleReference(reference: string): boolean {
  return reference.startsWith('urn:ccs:grid:');
}

export function isGridRuleValueReference(value: string): boolean {
  return value.startsWith('urn:ccs:value:');
}

export function encodeGridRuleTarget(target: GridTarget): string {
  return target.rowLinkId || target.columnId
    ? gridRuleTargetPrefix + encodeURIComponent(JSON.stringify(target))
    : target.gridLinkId;
}

export function decodeGridRuleTarget(reference: string): GridTarget | null {
  if (!isGridRuleReference(reference)) {
    return reference ? { gridLinkId: reference } : null;
  }
  if (!reference.startsWith(gridRuleTargetPrefix)) {
    return null;
  }
  const target = decodeGridRulePayload(reference.slice(gridRuleTargetPrefix.length));
  if (!target || typeof target.gridLinkId !== 'string' || !target.gridLinkId) {
    return null;
  }
  if (target.rowLinkId !== undefined && (typeof target.rowLinkId !== 'string' || !target.rowLinkId)) {
    return null;
  }
  if (target.columnId !== undefined && (typeof target.columnId !== 'string' || !target.columnId)) {
    return null;
  }
  return target as unknown as GridTarget;
}

export function encodeGridRuleValue(value: GridRuleValue): string {
  return gridRuleValuePrefix + encodeURIComponent(JSON.stringify(value));
}

export function decodeGridRuleValue(value: string): GridRuleValue | null {
  if (!value.startsWith(gridRuleValuePrefix)) {
    return null;
  }
  const decoded = decodeGridRulePayload(value.slice(gridRuleValuePrefix.length));
  if (!decoded || typeof decoded.columnId !== 'string' || !decoded.columnId) {
    return null;
  }
  if (!['selected', 'value', 'otherText'].includes(decoded.kind)) {
    return null;
  }
  if (decoded.kind !== 'selected' && typeof decoded.value !== 'string') {
    return null;
  }
  if (decoded.label !== undefined && typeof decoded.label !== 'string') {
    return null;
  }
  return decoded as unknown as GridRuleValue;
}

export function formatGridRuleValue(value: string): string {
  const decoded = decodeGridRuleValue(value);
  return decoded ? decoded.label || `${decoded.columnId}: ${decoded.value ?? 'Selected'}` : value;
}

function decodeGridRulePayload(payload: string): Record<string, any> | null {
  try {
    const parsed = JSON.parse(decodeURIComponent(payload));
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function getGridRuleOptions(schema: GridSchema, target: GridTarget): GridOption[] {
  const result = new Map<string, GridOption>();
  const rows = schema.rows.filter((row) => !target.rowLinkId || row.linkId === target.rowLinkId);
  const columns = schema.columns.filter((column) => !target.columnId || column.id === target.columnId);
  for (const column of columns) {
    for (const row of rows) {
      const properties = getGridCellProperties(schema, row.linkId, column.id);
      const selectedColumn =
        !schema.conditional &&
        ['radio', 'checkbox'].includes(properties.inputType) &&
        !(properties.inputType === 'checkbox' && target.columnId);
      const choices: (Omit<GridRuleValue, 'columnId'> & { label: string })[] = selectedColumn
        ? [{ kind: 'selected', label: String(properties.label || properties.id) }]
        : getGridCellOptions(schema, row.linkId, column.id)
            .filter((option) => properties.inputType !== 'checkbox' || target.columnId || option.value === 'true')
            .map((option) => ({ kind: 'value', value: option.value, label: option.label }));
      for (const option of choices) {
        const key = JSON.stringify([column.id, option.kind, option.value]);
        const label = target.columnId || option.kind === 'selected' ? option.label : `${column.label}: ${option.label}`;
        if (!result.has(key)) {
          result.set(key, { id: key, label, value: encodeGridRuleValue({ columnId: column.id, ...option, label }) });
        }
      }
    }
  }
  return [...result.values()];
}

export function getGridRuleValueError(schema: GridSchema, target: GridTarget, value: string): string | null {
  const token = decodeGridRuleValue(value);
  if (!token) {
    if (isGridRuleValueReference(value)) {
      return 'Rule value is invalid.';
    }
    return target.columnId ? null : 'Select a column for text values.';
  }
  if (!schema.columns.some((column) => column.id === token.columnId)) {
    return 'Grid column is no longer available.';
  }
  if (target.columnId && target.columnId !== token.columnId) {
    return 'Rule value belongs to another column.';
  }
  if (token.kind === 'otherText') {
    if (target.columnId !== token.columnId) {
      return 'Select a column for Other text.';
    }
    const available = schema.rows.some(
      (row) =>
        (!target.rowLinkId || row.linkId === target.rowLinkId) &&
        getGridOtherValue(getGridCellProperties(schema, row.linkId, token.columnId)) !== null
    );
    return available ? null : 'Other text is no longer available.';
  }
  const available = getGridRuleOptions(schema, target).some((option) => {
    const candidate = decodeGridRuleValue(option.value);
    return candidate?.columnId === token.columnId && candidate.kind === token.kind && candidate.value === token.value;
  });
  return available ? null : 'Grid option is no longer available.';
}
